import React from "react";
import { useTranslation } from "react-i18next";
import { UseCases } from "@planzone/core";
import { useMessage } from "@components/useMessage";
import { usePermissions } from "@scenes/safeguard";
import { ElementTypes, Props } from "./types";
import { ElementTypeSelect } from "./elementTypeSelect";
import { EnterIcon } from "@common/assets/enterIcon";
import {
  useCleanUp,
  useCreateElement,
  useRequestState,
  useTaskStatus,
} from "./utils";
import styles from "./styles.scss";

function useType(
  defaultType: ElementTypes,
): [
  ElementTypes,
  (value: ((prevState: ElementTypes) => ElementTypes) | ElementTypes) => void,
] {
  const permissions = usePermissions([
    UseCases.createTask,
    UseCases.createList,
    UseCases.createMilestone,
    UseCases.createEvent,
  ]);
  const map: { [Key in ElementTypes]: boolean } = {
    [ElementTypes.TASK]: permissions.canCreateTask,
    [ElementTypes.LIST]: permissions.canCreateList,
    [ElementTypes.MILESTONE]: permissions.canCreateMilestone,
    [ElementTypes.EVENT]: permissions.canCreateEvent,
  };
  const permissibleDefaultType = map[defaultType]
    ? defaultType
    : Object.entries(map).reduce<ElementTypes>(
        (t, [type, canCreate]) => (map[t] ? t : canCreate ? (type as any) : t),
        defaultType,
      );
  return React.useState<ElementTypes>(permissibleDefaultType);
}

function ElementCreationForm(props: Props) {
  const { t } = useTranslation();
  const [type, setType] = useType(props.defaultType || ElementTypes.TASK);
  const [name, setName] = React.useState("");
  const taskStatus = useTaskStatus(props.project);
  const isRequestPending = useRequestState(type);
  const create = useCreateElement(
    type,
    props.project,
    props.parentList,
    taskStatus,
  );
  const cleanUp = useCleanUp(props.parentList, props.range);

  useMessage(cleanUp(type, props.done));

  return (
    <div className={styles.elementCreationForm}>
      {props.loader && isRequestPending ? (
        props.loader(type, name)
      ) : (
        <React.Fragment>
          <div className={styles.menu}>
            <ElementTypeSelect selectedType={type} select={setType} />
          </div>
          <input
            type="text"
            className={styles.input}
            value={name}
            placeholder={
              type === ElementTypes.TASK
                ? t("listsList.quickCreation.createTask")
                : type === ElementTypes.LIST
                  ? t("listsList.quickCreation.createList")
                  : type === ElementTypes.MILESTONE
                    ? t("listsList.quickCreation.createMilestone")
                    : type === ElementTypes.EVENT
                      ? t("listsList.quickCreation.createEvent")
                      : ""
            }
            onChange={({ target }) => setName(target.value)}
            onKeyUp={({ key }) =>
              key === "Enter" && !isRequestPending && create(name, props.range)
            }
            autoFocus
          />
          <button
            className={styles.button}
            disabled={isRequestPending}
            onClick={() => !isRequestPending && create(name, props.range)}
          >
            <span>{t("listsList.quickCreation.submit")}</span>
            <EnterIcon className={styles.buttonIcon} />
          </button>
        </React.Fragment>
      )}
    </div>
  );
}

export { ElementCreationForm, ElementTypes };
